"use strict";
/**
 * Tab Session Manager
 *
 * Manages user sessions across multiple browser tabs.
 * Automatically logs out the user when the last tab is closed.
 * Uses BroadcastChannel API and localStorage for cross-tab communication.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.TabSessionManager = void 0;
const TAB_REGISTRY_KEY = "skysign_active_tabs";
const TAB_ID_KEY = "skysign_current_tab_id";
const TAB_CLOSE_CHANNEL = "skysign_tab_channel";
class TabSessionManager {
    constructor(onLastTabClose) {
        this.channel = null;
        this.registrationTimeout = null;
        this.tabId = this.generateTabId();
        this.onLastTabClose = onLastTabClose;
        // Bind event handlers once to maintain correct references for cleanup
        this.boundBeforeUnload = this.handleBeforeUnload.bind(this);
        this.boundStorageChange = this.handleStorageChange.bind(this);
        // Initialize BroadcastChannel for cross-tab communication
        if (typeof BroadcastChannel !== "undefined") {
            this.channel = new BroadcastChannel(TAB_CLOSE_CHANNEL);
            this.channel.onmessage = this.handleChannelMessage.bind(this);
        }
        // Delay registration to avoid interfering with FaceTec on page load/HMR
        // This prevents context switches during critical authentication flows
        this.registrationTimeout = setTimeout(() => {
            this.registerTab();
        }, 1000); // 1 second delay
        // Listen for tab close
        window.addEventListener("beforeunload", this.boundBeforeUnload);
        // Listen for storage changes (fallback for browsers without BroadcastChannel)
        window.addEventListener("storage", this.boundStorageChange);
    }
    /**
     * Generate unique tab ID
     */
    generateTabId() {
        // Try to get existing tab ID (for page refreshes)
        const existingId = sessionStorage.getItem(TAB_ID_KEY);
        if (existingId) {
            return existingId;
        }
        // Generate new UUID for new tab using the browser crypto API.
        const newId = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
            const random = window.crypto.getRandomValues(new Uint8Array(1))[0] & 15;
            const value = char === "x" ? random : (random & 3) | 8;
            return value.toString(16);
        });
        sessionStorage.setItem(TAB_ID_KEY, newId);
        return newId;
    }
    /**
     * Register this tab in the global registry
     */
    registerTab() {
        const tabs = this.getActiveTabs();
        if (!tabs.includes(this.tabId)) {
            tabs.push(this.tabId);
            this.saveActiveTabs(tabs);
        }
        // Don't broadcast on registration to avoid interfering with FaceTec sessions
        // Only broadcast on actual tab close
    }
    /**
     * Unregister this tab from the global registry
     */
    unregisterTab() {
        const tabs = this.getActiveTabs();
        const updatedTabs = tabs.filter((id) => id !== this.tabId);
        this.saveActiveTabs(updatedTabs);
        // Broadcast tab close
        this.broadcastMessage({ type: "tab_closed", tabId: this.tabId });
        // Check if this was the last tab
        if (updatedTabs.length === 0) {
            this.handleLastTabClose();
        }
    }
    /**
     * Get list of active tab IDs from localStorage
     */
    getActiveTabs() {
        try {
            const tabsJson = localStorage.getItem(TAB_REGISTRY_KEY);
            return tabsJson ? JSON.parse(tabsJson) : [];
        }
        catch (error) {
            console.error("Failed to parse active tabs:", error);
            return [];
        }
    }
    /**
     * Save list of active tab IDs to localStorage
     */
    saveActiveTabs(tabs) {
        localStorage.setItem(TAB_REGISTRY_KEY, JSON.stringify(tabs));
    }
    /**
     * Handle beforeunload event (tab close)
     */
    handleBeforeUnload() {
        this.unregisterTab();
    }
    /**
     * Handle BroadcastChannel messages
     */
    handleChannelMessage(event) {
        const { type, tabId } = event.data;
        if (type === "tab_closed" && tabId !== this.tabId) {
            // Another tab closed, update our local registry
            const tabs = this.getActiveTabs();
            const updatedTabs = tabs.filter((id) => id !== tabId);
            this.saveActiveTabs(updatedTabs);
        }
    }
    /**
     * Handle storage change events (fallback for BroadcastChannel)
     */
    handleStorageChange(event) {
        if (event.key === TAB_REGISTRY_KEY) {
            // Another tab modified the registry
            const tabs = this.getActiveTabs();
            // If no tabs remain, trigger logout
            if (tabs.length === 0) {
                this.handleLastTabClose();
            }
        }
    }
    /**
     * Handle last tab close event
     */
    handleLastTabClose() {
        // Call the logout callback
        this.onLastTabClose();
    }
    /**
     * Broadcast message to other tabs
     */
    broadcastMessage(message) {
        if (this.channel) {
            try {
                this.channel.postMessage(message);
            }
            catch (error) {
                console.error("Failed to broadcast message:", error);
            }
        }
    }
    /**
     * Cleanup when component unmounts
     */
    cleanup() {
        // Clear registration timeout if pending
        if (this.registrationTimeout) {
            clearTimeout(this.registrationTimeout);
            this.registrationTimeout = null;
        }
        // Remove event listeners using the bound references
        window.removeEventListener("beforeunload", this.boundBeforeUnload);
        window.removeEventListener("storage", this.boundStorageChange);
        // Close BroadcastChannel
        if (this.channel) {
            this.channel.close();
            this.channel = null;
        }
        // Note: We don't unregister the tab here, as unmount might be due to
        // navigation within the app, not actual tab close
    }
    /**
     * Get current tab ID
     */
    getTabId() {
        return this.tabId;
    }
    /**
     * Get count of active tabs
     */
    getActiveTabCount() {
        return this.getActiveTabs().length;
    }
}
exports.TabSessionManager = TabSessionManager;
exports.default = TabSessionManager;
